import { useCallback, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { AuthError } from '@mrrmlab/api-client';
import { UiProvider, ErrorBoundary, AppToastProvider, QueryErrorToaster, AuthErrorHandler } from '@mrrmlab/ui';
import { ListsApiClientProvider } from '@mrrmlab/feature-lists';
import { DayPlannerApiClientProvider } from '@mrrmlab/feature-day-planner';
import { TrackingApiClientProvider } from '@mrrmlab/feature-tracking';
import { MailApiProvider } from './mail/api-context';
import { DocumentsApiProvider } from './archiv/api-context';
import { RoadmapApiProvider } from './roadmap/api-context';
import { AppAuthContext } from './app-context';
import { loadEnv } from './env';
import { createAuth } from './services/auth';
import { createApi } from './services/api';
import { createAppRouter } from './router';
import { Sentry } from './sentry';

/**
 * Root component. Builds the singletons once (QueryClient, ApiClient,
 * AuthClient, Router) and wires them up with their providers. Rebuilt
 * never — useMemo with an empty dep array is the "construct once" pattern.
 */
export function App() {
  const { env, queryClient, router, api, auth } = useMemo(() => {
    const env = loadEnv();
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        },
      },
    });
    const auth = createAuth(env.oidc);
    const api = createApi(env.apiBaseUrl, auth);
    const router = createAppRouter({ queryClient, auth });
    return { env, queryClient, router, api, auth };
  }, []);

  // env is loaded for its side effect (throwing on missing vars at boot).
  void env;

  const isAuthError = useCallback((err: unknown) => err instanceof AuthError, []);
  const onAuthError = useCallback(() => {
    if (!auth) return;
    void auth.logout().then((logoutUrl) => {
      window.location.assign(logoutUrl ?? '/login');
    });
  }, [auth]);

  return (
    <ErrorBoundary onReset={() => window.location.reload()} onError={(error) => Sentry.captureException(error)}>
      <UiProvider>
        <AppToastProvider>
          <QueryClientProvider client={queryClient}>
            <QueryErrorToaster />
            <AuthErrorHandler isAuthError={isAuthError} onAuthError={onAuthError} />
            <ListsApiClientProvider client={api}>
              <DayPlannerApiClientProvider client={api}>
                <TrackingApiClientProvider client={api}>
                  <MailApiProvider value={api.mail}>
                    <DocumentsApiProvider value={api.documents}>
                      <RoadmapApiProvider value={api.roadmap}>
                        <AppAuthContext.Provider value={auth}>
                          <RouterProvider router={router} />
                        </AppAuthContext.Provider>
                      </RoadmapApiProvider>
                    </DocumentsApiProvider>
                  </MailApiProvider>
                </TrackingApiClientProvider>
              </DayPlannerApiClientProvider>
            </ListsApiClientProvider>
          </QueryClientProvider>
        </AppToastProvider>
      </UiProvider>
    </ErrorBoundary>
  );
}
